<template>
  <div>
    <h4>Appearance</h4>
    <sme-card large>
      <div class="card-container">
        <div class="card-design-uploads">
          <template v-if="showBanner">
            <div class="image-upload-container">
              <div class="label">
                Banner image
                <small>The background image shown for this award in the mobile app.</small>
              </div>

              <div class="image-upload-preview-container">
                <div class="banner-preview">
                  <template v-if="bannerPreviewUrl">
                    <img :src="bannerPreviewUrl" alt="Banner preview" class="image-upload-preview-image" />
                  </template>
                  <template v-else>
                    <div class="image-upload-preview-placeholder">
                      <font-awesome-icon :icon="['far', 'image']" size="2xl" />
                    </div>
                  </template>
                </div>
                <div class="image-upload-actions">
                  <b-button variant="primary" v-b-modal.banner-upload-modal size="sm">
                    {{ bannerPreviewUrl ? 'Upload another image' : 'Upload image' }}
                  </b-button>
                  <span v-b-tooltip.hover="!hasBannerGalleryImages ? 'No gallery images available' : ''" tabindex="0">
                    <b-button
                      variant="outline-primary"
                      v-b-modal.banner-gallery-modal
                      size="sm"
                      :disabled="!hasBannerGalleryImages"
                      >Choose from gallery</b-button
                    >
                  </span>
                  <b-button v-if="bannerPreviewUrl" variant="outline-danger" size="sm" @click="$emit('remove:banner')"
                    >Remove</b-button
                  >
                </div>
              </div>
            </div>

            <image-upload-modal
              modal-id="banner-upload-modal"
              title="Add banner image"
              :accepted-file-types="allowedImageTypes"
              :max-size-bytes="maxSizeBytes"
              :file="bannerFile"
              :preview-url="bannerPreviewUrl"
              supported-text="JPG or PNG • up to 10 MB • Recommended 1600 × 900 px (16:9)"
              @update:file="$emit('update:bannerFile', $event)"
              @update:previewUrl="$emit('update:bannerPreviewUrl', $event)"
            />

            <image-gallery-modal
              modal-id="banner-gallery-modal"
              image-type="banner"
              @select="$emit('gallerySelect:banner', $event)"
            />
          </template>

          <div class="image-upload-container">
            <div class="label">
              Award badge
              <small v-if="badgeCollectible">
                The image which represents this award in the portal and as an optional collectible item in the mobile
                app.
              </small>
              <small v-else>The image which represents this award in the portal.</small>
            </div>

            <div class="image-upload-preview-container">
              <div class="badge-preview">
                <template v-if="badgePreviewUrl">
                  <img :src="badgePreviewUrl" alt="Badge preview" class="image-upload-preview-image" />
                </template>
                <template v-else>
                  <div class="image-upload-preview-placeholder">
                    <font-awesome-icon :icon="['far', 'image']" size="2xl" />
                  </div>
                </template>
              </div>
              <div class="image-upload-actions">
                <b-button variant="primary" v-b-modal.badge-upload-modal size="sm">
                  {{ badgePreviewUrl ? 'Upload another image' : 'Upload image' }}
                </b-button>
                <span v-b-tooltip.hover="!hasBadgeGalleryImages ? 'No gallery images available' : ''" tabindex="0">
                  <b-button
                    variant="outline-primary"
                    v-b-modal.badge-gallery-modal
                    size="sm"
                    :disabled="!hasBadgeGalleryImages"
                    >Choose from gallery</b-button
                  >
                </span>
                <b-button v-if="badgePreviewUrl" variant="outline-danger" size="sm" @click="$emit('remove:badge')"
                  >Remove</b-button
                >
              </div>
            </div>
          </div>

          <image-upload-modal
            modal-id="badge-upload-modal"
            title="Add badge image"
            :accepted-file-types="allowedImageTypes"
            :max-size-bytes="maxSizeBytes"
            :file="badgeFile"
            :preview-url="badgePreviewUrl"
            preview-type="badge"
            supported-text="JPG or PNG • up to 10 MB • Recommended 300 × 300 px (1:1)"
            @update:file="$emit('update:badgeFile', $event)"
            @update:previewUrl="$emit('update:badgePreviewUrl', $event)"
          />

          <image-gallery-modal
            modal-id="badge-gallery-modal"
            image-type="logo"
            @select="$emit('gallerySelect:badge', $event)"
          />
        </div>
      </div>
    </sme-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import ApiClient from '@/ApiClient';
import SmeCard from '@/components/atoms/SmeCard.vue';
import ImageUploadModal from '@/pages/recognition/components/ImageUploadModal.vue';
import ImageGalleryModal from '@/pages/recognition/components/ImageGalleryModal.vue';

const hasBannerGalleryImages = ref(false);
const hasBadgeGalleryImages = ref(false);

onMounted(async () => {
  try {
    const data = await ApiClient.getRecognitionGalleryImages();
    hasBannerGalleryImages.value = Object.keys(data?.generic?.banner || {}).length > 0;
    hasBadgeGalleryImages.value = Object.keys(data?.generic?.logo || {}).length > 0;
  } catch {
    // leave both false — buttons stay disabled
  }
});

defineProps({
  bannerFile: {
    type: [Object, File],
    default: null,
  },
  bannerPreviewUrl: {
    type: String,
    default: '',
  },
  badgeFile: {
    type: [Object, File],
    default: null,
  },
  badgePreviewUrl: {
    type: String,
    default: '',
  },
  allowedImageTypes: {
    type: Array,
    default: () => ['image/jpeg', 'image/png'],
  },
  maxSizeBytes: {
    type: Number,
    default: 10 * 1024 * 1024, // 10MB
  },
  badgeCollectible: {
    type: Boolean,
    default: true,
  },
  showBanner: {
    type: Boolean,
    default: true,
  },
});

defineEmits([
  'update:bannerFile',
  'update:bannerPreviewUrl',
  'update:badgeFile',
  'update:badgePreviewUrl',
  'gallerySelect:banner',
  'gallerySelect:badge',
  'remove:banner',
  'remove:badge',
]);
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';
</style>
